import { styled } from "goober";
import { useLayoutEffect, useRef, useState } from "react";
import type { ConnectorConfig } from "../../lib/connectorDirectory";
import { routes, useMakeUrl } from "../../lib/urls";
import { ConnectorOption } from "../ConnectorOption";

const CARD_WIDTH = 216;
const CARD_HEIGHT = 57;
const GAP = 12;
const PADDING = 24;
const ROW_HEIGHT = CARD_HEIGHT + GAP;
const OVERSCAN = 2;

const List = styled("div")`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${CARD_WIDTH}px);
  gap: ${GAP}px;
  padding: ${PADDING}px;
`;

type Props = {
  apps: readonly ConnectorConfig[];
  disabled: boolean;
};

function useDirectoryWindow(count: number) {
  const listRef = useRef<HTMLDivElement>(null);
  const [window, setWindow] = useState({ columns: 1, start: 0, end: 1 });

  useLayoutEffect(() => {
    const scroll = listRef.current?.parentElement;
    if (!scroll) return;
    const update = () => {
      // client dimensions stay in CSS pixels when the app's UI zoom is applied.
      const columns = Math.max(
        1,
        Math.floor(
          (scroll.clientWidth - PADDING * 2 + GAP) / (CARD_WIDTH + GAP)
        )
      );
      const rows = Math.ceil(count / columns);
      const height = PADDING * 2 + rows * ROW_HEIGHT - GAP;
      const top = Math.min(
        scroll.scrollTop,
        Math.max(0, height - scroll.clientHeight)
      );
      const start = Math.max(
        0,
        Math.floor((top - PADDING) / ROW_HEIGHT) - OVERSCAN
      );
      const end = Math.min(
        rows,
        Math.ceil((top + scroll.clientHeight - PADDING) / ROW_HEIGHT) + OVERSCAN
      );
      setWindow((previous) =>
        previous.columns === columns &&
        previous.start === start &&
        previous.end === end
          ? previous
          : { columns, start, end }
      );
    };
    update();
    const resize = new ResizeObserver(update);
    resize.observe(scroll);
    scroll.addEventListener("scroll", update, { passive: true });
    return () => {
      resize.disconnect();
      scroll.removeEventListener("scroll", update);
    };
  }, [count]);

  return { listRef, ...window };
}

export function Directory({ apps, disabled }: Props) {
  const makeUrl = useMakeUrl();
  const { listRef, columns, start, end } = useDirectoryWindow(apps.length);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const focusedIndex = apps.findIndex((app) => app.identifier === focusedId);
  const focusedRow = Math.floor(focusedIndex / columns);
  const rows = Math.ceil(apps.length / columns);
  const visible = apps.flatMap((app, index) => {
    const row = Math.floor(index / columns);
    // Keep the focused row and its neighbors mounted for continuous Tab navigation.
    return (row >= start && row < end) ||
      (focusedIndex >= 0 && Math.abs(row - focusedRow) <= 1)
      ? [{ app, index, row }]
      : [];
  });

  return (
    <List
      ref={listRef}
      role="list"
      aria-label="Connector library"
      style={{ gridTemplateRows: `repeat(${rows}, ${CARD_HEIGHT}px)` }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget))
          setFocusedId(null);
      }}
    >
      {visible.map(({ app, index, row }) => (
        <div
          key={app.identifier}
          role="listitem"
          aria-posinset={index + 1}
          aria-setsize={apps.length}
          style={{ gridRow: row + 1, gridColumn: (index % columns) + 1 }}
          onFocus={() => setFocusedId(app.identifier)}
        >
          <ConnectorOption
            app={app}
            to={makeUrl(routes.connectors.browse.create, {
              identifier: app.identifier,
            })}
            disabled={disabled}
          />
        </div>
      ))}
    </List>
  );
}
